import {
  axi,
  getBackendAssetPath,
  GetListOutput,
  GetListParams,
} from '@shared/api';
import {TranslationDict} from '@shared/types';
import {validateData} from '@shared/utils';
import {Category, ExternalSubCategory, SubCategory} from '../types';
import {
  adminCategoryDetailV8nSchema,
  categoryV8nSchema,
  v8nSchemaOfGetAdminSubCategoriesResponse,
  v8nSchemaOfGetCategoriesResponse,
} from './backendSchemas';
import {
  AdminCategoryDetailOnBackend,
  CategoryOnBackend,
  GetAdminSubCategoriesResponse,
  GetCategoriesResponse,
} from './backendTypes';
import {
  mapAdminCategoryDetailFromBackend,
  mapAdminSubCategoryFromBackend,
  mapCategoryFromBackend,
} from './dataMappers';

export interface CreateCategoryParams {
  slug: string;
  nameTrans: TranslationDict;
  descriptionTrans: TranslationDict;
  logoUrl?: string;
  bannerUrl?: string;
  order?: number;
}

export interface UpdateCategoryParams {
  slug: string;
  nameTrans: TranslationDict;
  descriptionTrans: TranslationDict;
  logoUrl?: string;
  bannerUrl?: string;
  order?: number;
}

export const getCategories = async (): Promise<Category[]> => {
  const response = await axi.get<GetCategoriesResponse>('/admin/categories');

  try {
    validateData(
      response.data,
      v8nSchemaOfGetCategoriesResponse,
      'getCategories',
    );
  } catch {
    //
  }

  return response.data?.map(item => mapCategoryFromBackend(item)) ?? [];
};

export const getSubCategories = async (
  categoryId: string,
): Promise<SubCategory[]> => {
  const response = await axi.get<GetAdminSubCategoriesResponse>(
    `/admin/categories/${categoryId}/subcategories`,
  );

  try {
    validateData(
      response.data,
      v8nSchemaOfGetAdminSubCategoriesResponse,
      'getSubCategories',
    );
  } catch {
    //
  }

  return (
    response.data?.map(subCategory =>
      mapAdminSubCategoryFromBackend(subCategory),
    ) ?? []
  );
};

export const getCategory = async (id: string): Promise<Category> => {
  const response = await axi.get<AdminCategoryDetailOnBackend>(
    `/admin/categories/${id}`,
  );

  try {
    validateData(response.data, adminCategoryDetailV8nSchema, 'getCategory');
  } catch {
    //
  }

  return mapAdminCategoryDetailFromBackend(response.data);
};

const cleanTranslations = (
  trans: TranslationDict,
): Partial<TranslationDict> => {
  const result: Partial<TranslationDict> = {};
  Object.entries(trans).forEach(([lang, val]) => {
    if (typeof val === 'string' && val.trim()) {
      result[lang as keyof TranslationDict] = val;
    }
  });
  return result;
};

export const createCategory = async ({
  slug,
  nameTrans,
  descriptionTrans,
  logoUrl,
  bannerUrl,
  order,
}: CreateCategoryParams): Promise<void> => {
  await axi.post('/admin/categories', {
    slug,
    name: cleanTranslations(nameTrans),
    description: cleanTranslations(descriptionTrans),
    logo_url: logoUrl ? getBackendAssetPath(logoUrl) : undefined,
    banner_url: bannerUrl ? getBackendAssetPath(bannerUrl) : undefined,
    order: order ?? 0,
  });
};

export const updateCategory = async ({
  slug,
  nameTrans,
  descriptionTrans,
  logoUrl,
  bannerUrl,
  order,
}: UpdateCategoryParams): Promise<void> => {
  await axi.put(`/admin/categories/${slug}`, {
    name: cleanTranslations(nameTrans),
    description: cleanTranslations(descriptionTrans),
    logo_url: logoUrl ? getBackendAssetPath(logoUrl) : undefined,
    banner_url: bannerUrl ? getBackendAssetPath(bannerUrl) : undefined,
    order: order ?? 0,
  });
};

export interface CreateSubCategoryParams {
  categorySlug: string;
  slug: string;
  nameTrans: TranslationDict;
  geos?: string[];
  logoUrl?: string;
  bannerUrl?: string;
}

export interface UpdateSubCategoryParams {
  categorySlug: string;
  subCategorySlug: string;
  nameTrans: TranslationDict;
  geos?: string[];
  logoUrl?: string;
  bannerUrl?: string;
}

export const createSubCategory = async ({
  categorySlug,
  slug,
  nameTrans,
  geos,
  logoUrl,
  bannerUrl,
}: CreateSubCategoryParams): Promise<void> => {
  await axi.post(`/admin/categories/${categorySlug}/subcategories`, {
    slug,
    name: cleanTranslations(nameTrans),
    geos: geos ?? [],
    logo_url: logoUrl ? getBackendAssetPath(logoUrl) : undefined,
    banner_url: bannerUrl ? getBackendAssetPath(bannerUrl) : undefined,
  });
};

export const updateSubCategory = async ({
  categorySlug,
  subCategorySlug,
  nameTrans,
  geos,
  logoUrl,
  bannerUrl,
}: UpdateSubCategoryParams): Promise<void> => {
  await axi.put(
    `/admin/categories/${categorySlug}/subcategories/${subCategorySlug}`,
    {
      name: cleanTranslations(nameTrans),
      geos: geos ?? [],
      logo_url: logoUrl ? getBackendAssetPath(logoUrl) : undefined,
      banner_url: bannerUrl ? getBackendAssetPath(bannerUrl) : undefined,
    },
  );
};

interface ExternalSubCategoryOnBackend {
  id: string;
  external_id: string;
  name: string;
  category_slug: string;
}

const mapExternalSubCategoryFromBackend = ({
  id,
  external_id,
  name,
  category_slug,
}: ExternalSubCategoryOnBackend): ExternalSubCategory => ({
  id,
  externalId: external_id,
  name,
  categorySlug: category_slug,
});

interface GetUnmappedExternalSubCategoriesResponse {
  array: ExternalSubCategoryOnBackend[];
  page: number;
  per_page: number;
  total_pages: number;
  total: number;
}

export interface GetUnmappedExternalSubCategoriesParams extends GetListParams {
  categorySlug?: string;
}

export const getUnmappedExternalSubCategories = async ({
  categorySlug,
  page,
  pageSize,
}: GetUnmappedExternalSubCategoriesParams = {}): Promise<
  GetListOutput<ExternalSubCategory>
> => {
  const response = await axi.get<GetUnmappedExternalSubCategoriesResponse>(
    '/admin/subcategories/unmapped',
    {
      params: {
        category_slug: categorySlug || undefined,
        page,
        per_page: pageSize,
      },
    },
  );
  const {
    array,
    page: responsePage,
    per_page,
    total,
    total_pages,
  } = response.data;

  return {
    items: array.map(mapExternalSubCategoryFromBackend),
    page: responsePage,
    pageSize: per_page,
    total,
    totalPages: total_pages,
  };
};

export const getBoundExternalSubCategories = async (
  subCategoryId: string,
): Promise<ExternalSubCategory[]> => {
  const response = await axi.get<ExternalSubCategoryOnBackend[]>(
    `/admin/subcategories/${subCategoryId}/external`,
  );
  return response.data.map(mapExternalSubCategoryFromBackend);
};

export interface BindExternalSubCategoriesParams {
  subCategoryId: string;
  externalSubCategoryIds: string[];
}

export const bindExternalSubCategories = async ({
  subCategoryId,
  externalSubCategoryIds,
}: BindExternalSubCategoriesParams): Promise<void> => {
  await axi.post(`/admin/subcategories/${subCategoryId}/external`, {
    external_subcategory_ids: externalSubCategoryIds,
  });
};

export const unbindExternalSubCategory = async (
  subCategoryId: string,
  externalSubCategoryId: string,
): Promise<void> => {
  await axi.delete(
    `/admin/subcategories/${subCategoryId}/external/${externalSubCategoryId}`,
  );
};
